import React, { useContext, useState } from "react";
import { ReportParamsContext } from "../../context/ReportParamsContext";
import { getCallCompletion } from "../../API";

function completionBadge(pct, threshold) {
  if (pct >= threshold)
    return <span className="badge bg-success">{pct.toFixed(1)}%</span>;
  if (pct >= threshold * 0.8)
    return <span className="badge bg-warning text-dark">{pct.toFixed(1)}%</span>;
  return <span className="badge bg-danger">{pct.toFixed(1)}%</span>;
}

function ProgressBar({ pct, threshold }) {
  const color = pct >= threshold ? "bg-success" : pct >= threshold * 0.8 ? "bg-warning" : "bg-danger";
  return (
    <div className="progress" style={{ height: 8, minWidth: 80 }}>
      <div
        className={`progress-bar ${color}`}
        style={{ width: `${Math.min(pct, 100)}%` }}
      />
    </div>
  );
}

export default function CallCompletionReport() {
  const [{ fromDate, toDate, representatives, manufacturers }] = useContext(ReportParamsContext);

  const [threshold, setThreshold] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedReps, setExpandedReps] = useState(new Set());

  const handleGenerate = async () => {
    if (!fromDate || !toDate) {
      setError("Please select at least one month in the parameters bar.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await getCallCompletion({
        fromDate,
        toDate,
        threshold,
        representativeIds: representatives.map((r) => r.id),
        manufacturerIds: manufacturers.map((m) => m.id),
      });
      setData(result);
      setExpandedReps(new Set());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleRep = (repId) => {
    setExpandedReps((prev) => {
      const next = new Set(prev);
      next.has(repId) ? next.delete(repId) : next.add(repId);
      return next;
    });
  };

  const totalActual   = data?.reps.reduce((s, r) => s + r.totalActual,   0) ?? 0;
  const totalExpected = data?.reps.reduce((s, r) => s + r.totalExpected, 0) ?? 0;
  const overallPct    = totalExpected > 0 ? Math.min(totalActual / totalExpected * 100, 100) : 0;
  const flaggedCount  = data?.reps.filter((r) => r.flagged).length ?? 0;

  return (
    <div className="flex-grow-1 overflow-auto">
      <div className="container-fluid py-4 px-4">

        <div className="card shadow-sm mb-4">
          <div className="card-body p-4">
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <label className="form-label fw-medium mb-1">
                  Completion threshold (%)
                </label>
                <input
                  type="number"
                  className="form-control"
                  min={1}
                  max={100}
                  value={threshold}
                  onChange={(e) => setThreshold(Number(e.target.value))}
                />
              </div>
              <div className="col-md-auto">
                <button
                  className="btn btn-primary"
                  onClick={handleGenerate}
                  disabled={loading}
                >
                  {loading ? "Loading…" : "Generate Report"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {data && (
          <>
            <div className="row g-3 mb-4">
              <div className="col-sm-4">
                <div className="card text-center shadow-sm h-100">
                  <div className="card-body py-3">
                    <div className="fs-4 fw-bold">{totalActual}</div>
                    <div className="text-muted small">Actual Visits</div>
                  </div>
                </div>
              </div>
              <div className="col-sm-4">
                <div className="card text-center shadow-sm h-100">
                  <div className="card-body py-3">
                    <div className="fs-4 fw-bold">{totalExpected.toFixed(1)}</div>
                    <div className="text-muted small">
                      Expected Visits ({data.periodDays} days)
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-sm-4">
                <div className="card text-center shadow-sm h-100">
                  <div className="card-body py-3">
                    <div className="fs-4 fw-bold">
                      {completionBadge(overallPct, data.threshold)}
                    </div>
                    <div className="text-muted small">
                      Overall Completion
                      {flaggedCount > 0 && (
                        <span className="ms-2 badge bg-danger">{flaggedCount} rep{flaggedCount > 1 ? "s" : ""} below target</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {data.reps.length === 0 ? (
              <div className="alert alert-info">
                No visits found for this period. Make sure stores have a visit frequency configured.
              </div>
            ) : (
              <div className="table-responsive">
                <table
                  className="table table-hover table-bordered mb-0"
                  style={{ fontSize: "0.85rem" }}
                >
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 28 }} />
                      <th>Representative</th>
                      <th className="text-end" style={{ whiteSpace: "nowrap" }}>Actual</th>
                      <th className="text-end" style={{ whiteSpace: "nowrap" }}>Expected</th>
                      <th style={{ minWidth: 140, whiteSpace: "nowrap" }}>Completion</th>
                      <th style={{ whiteSpace: "nowrap" }}>Stores</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.reps.map((rep) => (
                      <React.Fragment key={rep.repId}>
                        <tr
                          className={rep.flagged ? "table-danger" : ""}
                          style={{ cursor: "pointer" }}
                          onClick={() => toggleRep(rep.repId)}
                        >
                          <td className="text-center text-muted" style={{ userSelect: "none" }}>
                            {expandedReps.has(rep.repId) ? "▾" : "▸"}
                          </td>
                          <td className="fw-medium">{rep.repName || "(Unknown)"}</td>
                          <td className="text-end">{rep.totalActual}</td>
                          <td className="text-end">{rep.totalExpected.toFixed(1)}</td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              {completionBadge(rep.completionPct, data.threshold)}
                              <div className="flex-grow-1">
                                <ProgressBar pct={rep.completionPct} threshold={data.threshold} />
                              </div>
                            </div>
                          </td>
                          <td className="text-muted">{rep.stores.length} store{rep.stores.length !== 1 ? "s" : ""}</td>
                        </tr>

                        {expandedReps.has(rep.repId) &&
                          rep.stores.map((store) => (
                            <tr
                              key={store.storeId}
                              className={store.flagged ? "table-warning" : "table-light"}
                              style={{ fontSize: "0.8rem" }}
                            >
                              <td />
                              <td colSpan={1} className="ps-4 text-muted" style={{ whiteSpace: "nowrap" }}>
                                {store.bannerName && (
                                  <span className="badge bg-secondary me-1">{store.bannerName}</span>
                                )}
                                {store.storeNumber && <span className="me-1">#{store.storeNumber}</span>}
                                {store.storeAddress}
                              </td>
                              <td className="text-end">{store.actualVisits}</td>
                              <td className="text-end">{store.expectedVisits.toFixed(1)}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  {completionBadge(store.completionPct, data.threshold)}
                                  <div className="flex-grow-1">
                                    <ProgressBar pct={store.completionPct} threshold={data.threshold} />
                                  </div>
                                </div>
                              </td>
                              <td className="text-muted">
                                {store.city}
                                <span className="ms-2 text-muted" style={{ fontSize: "0.75rem" }}>
                                  ({store.visitFrequency}×/yr)
                                </span>
                              </td>
                            </tr>
                          ))
                        }
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
